import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { TokenStorageService } from 'src/app/core/security/token-storage-service';
import * as _ from 'lodash';
import { TokenResponse } from 'src/app/shared/models/token-response.model';
import { Storage } from './storage.service';
import { StorageCollections } from '../enum/storage-collections.enum';
import { environment } from 'src/environments/environment';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ConfigService } from './config.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { CurrentUserComponent } from 'src/app/components/current-user/current-user.component';
import { UserIdleService } from 'angular-user-idle';
// declare const ga: any;
@Injectable()
export class CurrentUserService {

  constructor(
    private authHttp: HttpClient,
    private router: Router,
    private tokenStore: TokenStorageService,
    private config: ConfigService,
    private storage: Storage,
    private errorHandlerService: ErrorHandlerService,
    private modal: ModalService,
    private userIdle: UserIdleService
  ) { }

  private tokenChecker: any;

  public user: any = null;
  public isInternalAdmin = false;
  public isInternal = false;
  public tokenExpiration: any;
  public currentTime: number;
  public displayName = '';
  public customerSettings = new BehaviorSubject({});
  public customerSettings$ = this.customerSettings.asObservable();
  public routeAfterLogin: string = null;
  public entitlements: string[] = [];
  public modalShown = false;
  public isImpersonator: boolean = false;

  public async getUser(): Promise<any> {
    if (!this.user) {
      try {
        const userProfile = await this.storage.getItem('user');
        if (!userProfile) {
          return this.logout();
        }
        try {
          this.user = JSON.parse(userProfile);
        } catch (error) {
          this.user = userProfile
        }
        this.entitlements = _.get(this.user, 'userEntitlements').map(entitlement => entitlement.code);
        this.isInternalAdmin = this.user.role.code === 'HC_INTERNAL_ADMIN';
        if (this.user.role.code.startsWith('HC_INTERNAL') && this.user.role.code.endsWith('ADMIN')) {
         this.isInternal = true;
        } else {
         this.isInternal = false;
        }

        this.displayName = `${this.user.firstname} ${this.user.lastname}`;
      } catch (e) {
      }
      return Promise.resolve(this.user);
    }

    return Promise.resolve(this.user);
  }

  public async setIsImpersonator() {
    try {
      this.isImpersonator = Boolean(await this.tokenStore.isImpersonator());
      return this.isImpersonator;
    } catch(error) {
      this.isImpersonator = false;
      return this.isImpersonator;
    }
  }

  public async getUserInfo() {
    let roleChecker = await this.storage.getItem('user');
    if (!roleChecker) {
      return {};
    }
    return JSON.parse(roleChecker);
  }

  public async refreshToken() {
    const config = await this.config.getConfig();
    const body = {
      refresh_token: await this.tokenStore.getRefreshToken(),
      grant_type: "refresh_token"
    };
    const headers = new HttpHeaders({
      'client_id': config.runtime.authClientId
    });
    return this.authHttp
      .post(`${config.runtime.authBaseUrl}/azure-ad-b2c/jwt`, body, { headers: headers })
      .toPromise()
      .then(async (response: TokenResponse) => {
        this.tokenExpiration = Date.now() + response.expires_in * 1000;
        await this.tokenStore.setAccessToken(response.access_token);
        await this.tokenStore.setRefreshToken(response.refresh_token);
        await this.storage.setItem('expire_time', this.tokenExpiration);
        return response;
      }).catch((e) => {
        this.logout();
      });
  }

  public async checkEntitlements(entitlements: string[]) {
    if (!(await this.tokenStore.hasAccessToken())) {
      return false;
    } else if (this.tokenStore.tokenExpired()) {
      this.refreshToken();
      this.checkEntitlements(entitlements);
    }
    let userEntitlements = await this.getEntitlements() || {};

    const userEntitlementsArray = Object.keys(userEntitlements).map(function(key) {
      if (userEntitlements[key]) return key;
    }) || [];

    return _.intersection(userEntitlementsArray, entitlements).length === entitlements.length || this.isInternalAdmin;
  }

  public async getEntitlements() {
    let entitlements = await this.storage.getItem('entitlements');
    try {
      entitlements = JSON.parse(entitlements);
    } catch (error) {
      entitlements = null;
    };
    if (!entitlements) {
      let user = await this.storage.getItem('user');
      try {
        user = JSON.parse(user);
      } catch (error) {
        return {};
      }
      let temp = {};
      user['userEntitlements'].forEach((i) => {
        temp[i.code] = i.status;
      });
      await this.storage.setItem('entitlements', temp);
      return temp;
    }
  }

  public checkToken() {
    if (this.tokenStore.tokenExpired()) {
      this.tokenExpiration = Number(localStorage.getItem('expire_time'));
    }
    const currentTime = Date.now();
    if (!this.tokenExpiration || (this.tokenExpiration + 1 * 60 * 1000) <= currentTime) {
      this.modalShown = false;
      this.logout();
      return false;
    } else if (this.tokenExpiration <= currentTime) {
      this.show();
      if (this.tokenExpiration === -1) {
        return false;
      }
      return true;
    } else {
      return true;
    }
  }

  public show() {
    this.modalShown = true;
  }

  // Remove tokens and redirect to login
  public async logout() {
    if (!this.router.url.includes('request-access')) { // don't try to redirect if on request-access page 
      try {
        await this.clearUserContext();
        await this.storage.truncateDB();
      } catch (err) {
        console.log("Error Logging out, ", err.message);
      } finally {
        const isCordovaApp = document.URL.indexOf('http://') === -1 && document.URL.indexOf('https://') === -1;
        if (isCordovaApp) {
          window.location.href = `../index.html`;
        } else {
          window.location.href = window.location.origin;
        }
        this.tokenChecker = null;
      }
    }
  }

  private async clearUserContext() {
    await this.tokenStore.clearUserContext();
    this.user = null;
  }

  private redirectToLogin(): void {
    window.location.href = window.location.origin;
  }

  public resetPassword(email): Promise<any> {
    return this.authHttp.post(`${environment.runtime.userManagementApiUrl}/user/resetPassword/${email}`, {})
      .toPromise()
      .then((response: any) => response)
      .catch(e => {
        return this.errorHandlerService.handleError(e, e.message);
      });
  }

  public async refresh() {
    await this.refreshToken().catch(e => {
      console.log(e);
    })
  }

  public async initalizeLogin() {
    this.user = await this.storage.getItem('user');
    if (!this.user) {
      return this.logout();
    }
    this.user = JSON.parse(this.user)
    await this._setTokenExpirationCheck();
    this._startWatching();
    // invoked when user remains idle for 30mins
    this.userIdle.onTimerStart().subscribe(count => {
      // After 1 sec of idle time, show the 'stay loggedin' popup
      if (count === 1) {
        this.modal.open(CurrentUserComponent);
      }
    });
    // Invoked after timeout period i.e, 5 mins, After showing the 'stay loggedin' popup for 5 mins logout.
    this.userIdle.onTimeout().subscribe(() => {
      this.logout();
    });
  }

  private async _setTokenExpirationCheck() {
    let expireTime = await this.storage.getItem('expire_time');
    let checkTime = 55 * 60 * 1000; // 55 minutes
    let alertTime = expireTime - checkTime; // the time the token expires minus 5 minutes
    if (!this.storage.getItem('access_token')) {
      this.logout();
    } else {
      this.tokenChecker = setInterval((() => {
        this.storage.removeItem(StorageCollections.SECURITY, 'access_token');
        this.refresh();
      }), checkTime);
    }
  }

  // Start watching for user inactivity.
  private _startWatching() {
    this.userIdle.startWatching();
  }
  // Restart IdleTime Timer
  public resetSessionTimer() {
    this.userIdle.resetTimer();
  }

  public editUser(): Promise<{}[]> {
    let userInfo = {
      name: `${this.user.firstname} ${this.user.lastname}`,
      email: this.user.email,
      phone: this.user.phone,
      mobile_phone: this.user.alternatePhone
    };

    return this.authHttp.post(`${environment.runtime.userManagementApiUrl}/user/update/self`, userInfo)
      .toPromise()
      .then(async (response: any) => {
        await this.storage.setItem('user', JSON.stringify(this.user));
        return response;
      })
      .catch(e => this.errorHandlerService.handleError(e, e.message || e.message));
  }

  public setNotificationPreferences(preferences) {
    let data = {
      entityType:"shipment",
      customerId: this.user.organizationCode,
      entityIds:[""],
      preference: preferences
    };
    return this.authHttp.post(`${environment.runtime.hubconnectPortalUrl}/hubconnectapi/user/notification-preference`, data)
      .toPromise()
      .then((response: any) => {
        return response;
      })
      .catch(e => this.errorHandlerService.handleError(e, e.message));
  }

  public getNotificationPreferences() {
    return this.authHttp.get(`${environment.runtime.hubconnectPortalUrl}/hubconnectapi/user/notification-preference/shipment`)
      .toPromise()
      .then((response: any) => {
        if (response && response.preference) {
          return response.preference;
        }
        return {};
      })
      .catch(e => this.errorHandlerService.handleError(e, e.message));
  }

  public toTitleCase(persona: string = 'Customer') {
    return persona.replace(
      /\w\S*/g,
      function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
  }
}
